import { File as AppFile } from "@/types/breeze/File";
import { fileService } from "@/services/breeze/FileService";

/**
 * Comprehensive utility functions for file handling operations
 */

// ============================================================================
// SIZE & FORMATTING UTILITIES
// ============================================================================

/**
 * Format a byte count with a sensible unit so small files don't render as "0.00 MB".
 * Returns a complete string including the unit (e.g. "12 B", "4.3 KB", "1.20 MB", "2.5 GB").
 */
export const formatFileSize = (sizeInBytes: number): string => {
  if (!Number.isFinite(sizeInBytes) || sizeInBytes <= 0) return "0 B";
  const KB = 1024;
  const MB = KB * 1024;
  const GB = MB * 1024;
  if (sizeInBytes < KB) return `${sizeInBytes} B`;
  if (sizeInBytes < MB) return `${(sizeInBytes / KB).toFixed(1)} KB`;
  if (sizeInBytes < GB) return `${(sizeInBytes / MB).toFixed(2)} MB`;
  return `${(sizeInBytes / GB).toFixed(2)} GB`;
};

/**
 * Convert MB to bytes
 */
export const mbToBytes = (sizeInMB: number): number => {
  return sizeInMB * 1024 * 1024;
};

/**
 * Convert bytes to MB (rounded)
 */
export const bytesToMB = (sizeInBytes: number): number => {
  return Math.round(sizeInBytes / (1024 * 1024));
};

export const formatDuration = (seconds: number | null | undefined): string | null => {
  if (seconds == null || seconds <= 0) return null;
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  return `${m}:${s.toString().padStart(2, "0")}`;
};

export const formatDimensions = (width: number | null | undefined, height: number | null | undefined): string | null =>
  width && height ? `${width}×${height}` : null;

export type MediaCategory = "image" | "video" | "audio" | "document" | "other";

// Categories supported by the upload pipeline and surfaced in the file-type filter.
// "other" is intentionally excluded — we filter to types users can actually upload.
export const UPLOADABLE_MEDIA_CATEGORIES: Exclude<MediaCategory, "other">[] = [
  "image",
  "video",
  "audio",
  "document",
];

export const MEDIA_CATEGORY_LABELS: Record<Exclude<MediaCategory, "other">, string> = {
  image: "Images",
  video: "Video",
  audio: "Audio",
  document: "Documents",
};

export const resolveMediaCategory = (file: { media_category?: MediaCategory | null; file_type?: string | null }): MediaCategory => {
  if (file.media_category) return file.media_category;
  const mime = (file.file_type || "").toLowerCase();
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("video/")) return "video";
  if (mime.startsWith("audio/")) return "audio";
  if (mime.startsWith("application/pdf") || mime.startsWith("text/") || mime.includes("document") || mime.includes("spreadsheet") || mime.includes("presentation")) return "document";
  return "other";
};

export const isPreviewable = (file: { media_category?: MediaCategory | null; file_type?: string | null }): boolean => {
  const cat = resolveMediaCategory(file);
  return cat === "image" || cat === "video" || cat === "audio";
};

export const formatBitrate = (bps: number | null | undefined): string | null => {
  if (!bps || bps <= 0) return null;
  if (bps >= 1_000_000) return `${(bps / 1_000_000).toFixed(1)} Mbps`;
  return `${Math.round(bps / 1000)} kbps`;
};

// ============================================================================
// VALIDATION UTILITIES
// ============================================================================

/**
 * Validate file types against accepted formats
 */
export const validateFileTypes = (files: File[], accept: string): File[] => {
  if (accept === "*/*") return [];

  const acceptTypes = accept.split(",").map((type) => type.trim());

  return files.filter((file) => {
    return !acceptTypes.some((type) => {
      if (type.startsWith(".")) {
        return file.name.toLowerCase().endsWith(type.toLowerCase());
      }
      return file.type.match(new RegExp(type.replace("*", ".*")));
    });
  });
};

/**
 * Validate file sizes against max size
 */
export const validateFileSizes = (
  files: File[],
  maxSizeInBytes: number,
): File[] => {
  return files.filter((file) => file.size > maxSizeInBytes);
};

/**
 * Get field max files constraint
 */
export const getMaxFiles = (fieldDef?: {
  options?: { file?: { max_files?: number } | null } | null;
}): number => {
  return fieldDef?.options?.file?.max_files || 1;
};

// ============================================================================
// CONTENT UTILITIES
// ============================================================================

/**
 * Check if an item has images
 * @param item - The item to check for images
 * @returns True if the item has an images array with at least one element
 */
export function hasImages(item: { images?: unknown[] }): boolean {
  return Boolean(
    item.images && Array.isArray(item.images) && item.images.length > 0,
  );
}

// ============================================================================
// DOWNLOAD UTILITIES
// ============================================================================

/**
 * Downloads a file securely with JWT authentication
 * @param fileUrl The file URL that requires authentication
 * @param fileName The desired filename for the download
 */
export async function downloadSecureFile(
  id: number,
  fileName: string,
): Promise<void> {
  try {
    const url = await fileService.getFileSignedUrl(id);

    // Cross-origin `download` attribute is ignored by browsers, so for downloads we ask the
    // user agent to navigate to the presigned URL. The S3 response headers determine whether
    // it saves vs. opens inline; for forced downloads we'd need a server-side Content-Disposition.
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.target = "_blank";
    link.rel = "noopener";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error("Failed to download file:", error);
    throw new Error("Failed to download file");
  }
}

/**
 * Downloads a file securely using JWT authentication
 * @param file - The file to download
 */
export async function downloadFile(file: AppFile): Promise<void> {
  try {
    await downloadSecureFile(file.id, file.original_file_name);
  } catch (error) {
    console.error("Error downloading file:", error);
    throw error;
  }
}
